import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Flag from "react-world-flags";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaCheckCircle,
  FaDownload,
  FaPhone,
  FaLock,
  FaChevronDown,
  FaCalendarCheck,
} from "react-icons/fa";

/* ================= BRAND ================= */
const PRIMARY_GRADIENT =
  "bg-gradient-to-r from-primary-soft via-primary to-primary-soft bg-clip-text text-transparent animate-gradient";

/* ================= FAQ DATA ================= */
const FAQ_DATA = [
  {
    question: "How long does it take to complete each level?",
    answer:
      "Typically, each CEFR level (A1, A2, B1, B2) takes 2-3 months for weekday batches and 3-4 months for weekend batches. Advanced levels (C1, C2) may take slightly longer due to the complexity.",
  },
  {
    question: "Do I need any prior French knowledge to start?",
    answer:
      "No prior knowledge is required for A1 level. We offer beginner-friendly courses that start from the basics. For intermediate and advanced levels, we conduct a placement test to ensure you join the appropriate level.",
  },
  {
    question: "What is the difference between DELF, DALF, TCF, and TEF?",
    answer:
      "DELF (A1-B2) and DALF (C1-C2) are diplomas valid for life. TCF and TEF are proficiency tests (valid 2 years) primarily used for Canadian immigration, citizenship, and university admissions. We offer specialized preparation for all these exams.",
  },
  {
    question: "Are the classes conducted online or offline?",
    answer:
      "We offer both online and offline classes. Our online classes are conducted via Zoom with live interaction, screen sharing, and breakout rooms for practice. Offline classes are available at our centers in major cities across India.",
  },
  {
    question: "Will I get a certificate after course completion?",
    answer:
      "Yes, you will receive a course completion certificate from The Lingua Alliance. Additionally, we prepare you for official DELF/DALF/TCF/TEF certifications issued by recognized international bodies, which are accepted globally.",
  },
  {
    question: "What if I miss a class?",
    answer:
      "All classes are recorded and available for lifetime access. If you miss a live session, you can watch the recording at your convenience. We also offer make-up classes for missed sessions whenever possible.",
  },
  {
    question: "Do you provide study materials?",
    answer:
      "Yes, comprehensive study materials including textbooks, practice worksheets, vocabulary flashcards, and digital resources are provided. You'll also get access to our online learning platform with additional resources.",
  },
  {
    question: "Can I get a refund if I'm not satisfied?",
    answer:
      "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with the course quality within the first 30 days, you can request a full refund with no questions asked.",
  },
];

/* ================= COMPONENT ================= */
export default function French() {
  const formRef = useRef(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  /* Initialize AOS */
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
    });
  }, []);

  /* Sticky stop before footer */
  useEffect(() => {
    const handler = () => {
      const form = formRef.current;
      const footer = document.getElementById("page-footer");
      if (!form || !footer) return;

      const scrollY = window.pageYOffset;
      const footerTop = footer.getBoundingClientRect().top + scrollY;
      const formHeight = form.offsetHeight;
      const stopPoint = footerTop - formHeight - 24;

      if (scrollY >= stopPoint) {
        form.style.position = "absolute";
        form.style.top = `${stopPoint - form.parentElement.offsetTop}px`;
      } else {
        form.style.position = "sticky";
        form.style.top = "24px";
      }
    };

    window.addEventListener("scroll", handler);
    window.addEventListener("resize", handler);
    handler();

    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900">
      {/* ================= HERO ================= */}
      <header className="relative min-h-[550px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black/50" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 text-white">
          <div className="max-w-3xl space-y-6">
            <div className="text-sm text-white/80">
              <Link to="/" className="hover:text-white">
                Home
              </Link>{" "}
              / Language Programs / <strong>French</strong>
            </div>

            <div className="flex items-center gap-3">
              <Flag code="FR" style={{ width: 40, height: 28 }} />
              <span className="text-xs uppercase tracking-widest text-white/80">
                Learn French with The Lingua Alliance
              </span>
            </div>

            <h1
              className={`text-4xl md:text-5xl pb-2 font-extrabold text-white`}
            >
              TLA-Aligned DELF Success Programs and TEF Mastery
            </h1>

            <p className="text-lg text-white/90 max-w-2xl">
              Clear, well-planned learning paths for students and adults who
              want to master French and shine in their DELF certification.
            </p>

            <div className="flex flex-wrap gap-4 pt-3">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-all hover:shadow-lg hover:-translate-y-0.5">
                <FaDownload className="text-lg" />
                Download Brochure
              </button>
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-soft via-primary to-primary-soft text-white font-semibold rounded-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                <FaCalendarCheck className="text-lg" />
                Book Free Demo
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ================= STATS BAR ================= */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="0"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">11+</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Years of Expertise
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  4,000+
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Students Across Globe
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">✓</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Certified Experts
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  All Ages
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Programs Available
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MAIN GRID ================= */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_360px] gap-12">
        {/* ================= LEFT CONTENT ================= */}
        <main className="space-y-16">
          {/* TEF / TCF */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              TEF / TCF
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <ContentCard
                title="Listening Comprehension (40 minutes)"
                text="Assess your understanding of spoken French through real-life audio clips, including dialogues, news, and announcements. Includes 40 MCQs."
              />
              <ContentCard
                title="Reading Comprehension (60 minutes)"
                text="Develop the ability to understand written French across different levels of complexity. You will answer 40 questions based on articles, notices, and various reading passages."
              />
              <ContentCard
                title="Oral Expression (15 minutes)"
                text="Speak confidently by sharing your ideas, describing images, and participating in role-play conversations. Complete 2 speaking tasks."
              />
              <ContentCard
                title="Written Expression (60 minutes)"
                text="Produce written responses such as summaries, opinion pieces, or formal letters. Complete 2 writing tasks that assess clarity, coherence, and structure."
              />
            </div>
          </section>

          {/* DELF */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              TLA-Aligned DELF Training
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <LevelItem
                title="A1 Level – Beginner | 40 Hours"
                text="Learn key French phrases, self-introductions, and practical daily expressions for confident basic communication."
              />
              <LevelItem
                title="A2 Level (Elementary) | 40 Hours"
                text="Communicate in familiar situations and take part in brief conversations with improved clarity and confidence."
              />
              <LevelItem
                title="B1 Level (Intermediate) | 60 Hours"
                text="Communicate comfortably, express opinions, and manage real-life conversations in French."
              />
              <LevelItem
                title="B2 Level (Upper Intermediate) | 80 Hours"
                text="Read deeply, argue clearly, and communicate with fluency in higher-level contexts."
              />
            </div>
          </section>

          {/* DALF */}
          <section>
            <h2 className={`text-3xl font-bold mb-2 ${PRIMARY_GRADIENT}`}>
              TLA-Aligned DALF Training
            </h2>
            <p className="text-gray-700 mb-6">
              Master advanced French for top-tier academic and workplace
              performance.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <LevelItem
                title="C1 Level (Advanced) | 60 Hours"
                text="Speak and write with clarity, analyse complex texts deeply, and participate in high-level debates and academic interactions."
              />
              <LevelItem
                title="C2 Level (Mastery) | 60 Hours"
                text="Near-native mastery for reading, writing, and speaking with precision and sophistication across all domains."
              />
            </div>
          </section>

          {/* UNIQUE */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              What Makes Us Unique
            </h2>

            <ul className="grid md:grid-cols-2 gap-4 text-gray-700">
              <ListItem text="Expert-Led Learning from language professionals" />
              <ListItem text="Globally-Aligned courses with international standards" />
              <ListItem text="Flexible learning with live & recorded sessions" />
              <ListItem text="Affordable excellence without compromise" />
              <ListItem text="Complete DELF & DALF certification support" />
            </ul>
          </section>

          {/* COMPARISON */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              French Language: How It Compares to English
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <Comparison
                label="Speaking"
                value="Moderate – Easy to get your words flowing"
              />
              <Comparison
                label="Grammar"
                value="Moderate – Rules are manageable with practice"
              />
              <Comparison
                label="Writing"
                value="Moderate-Easy – Compose sentences with confidence"
              />
              <Comparison
                label="Overall Difficulty"
                value="Moderate – Achievable for motivated learners"
              />
            </div>
          </section>

          {/* FAQ SECTION */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              Frequently Asked Questions
            </h2>

            <div className="space-y-3">
              {FAQ_DATA.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h4>
                    <FaChevronDown
                      className={`text-primary flex-shrink-0 transition-transform duration-200 ${
                        expandedFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 border-t border-gray-100">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* ================= RIGHT STICKY FORM ================= */}
        <aside className="relative">
          <div ref={formRef} className="hidden lg:block sticky top-24">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
              {/* Header Badge */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full mb-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    LIMITED COHORT · A1-C2
                  </span>
                </div>
                <h3 className={`text-2xl font-bold ${PRIMARY_GRADIENT}`}>
                  French Language Program
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  Live Classes · Projects · Exam Prep Included
                </p>
              </div>

              {/* Pricing */}
              <div className="text-center mb-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="text-4xl font-extrabold text-primary">
                  ₹60,000/-
                </div>
                <div className="text-sm text-gray-400 line-through">
                  ₹70,000/-
                </div>
                <div className="mt-2 text-xs text-gray-600 font-medium">
                  💼 Weekday: 2 Months | Weekend: 3 Months
                </div>
              </div>

              {/* Form */}
              <form className="space-y-4">
                <input
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg py-3 px-4 outline-none text-sm focus:border-primary focus:bg-white transition"
                  placeholder="Full Name"
                  type="text"
                />
                <input
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg py-3 px-4 outline-none text-sm focus:border-primary focus:bg-white transition"
                  placeholder="Email Address"
                  type="email"
                />
                <input
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg py-3 px-4 outline-none text-sm focus:border-primary focus:bg-white transition"
                  placeholder="Mobile Number"
                  type="tel"
                />
                <button
                  type="button"
                  className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-primary-soft via-primary to-primary-soft shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Enroll Now
                </button>
              </form>

              {/* Guarantees */}
              <div className="mt-5 text-xs text-gray-600 text-center space-y-2">
                <div className="flex justify-center items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>30-Day Money-Back Guarantee</span>
                </div>
                <div className="flex justify-center items-center gap-2 text-gray-500">
                  <FaLock className="text-xs" />
                  <span>Secure & No-obligation Consultation</span>
                </div>
              </div>

              {/* Contact */}
              <div className="mt-6 text-center pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-600 mb-2">
                  Prefer talking to a counsellor?
                </div>
                <a
                  href="tel:+919123408959"
                  className="inline-flex items-center gap-2 text-sm bg-blue-50 px-4 py-2 rounded-lg text-primary font-semibold border border-blue-200 hover:bg-blue-100 transition"
                >
                  <FaPhone /> +91-9123408959
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ================= SMALL COMPONENTS ================= */

function ContentCard({ title, text }) {
  return (
    <div
      className="bg-gradient-to-br from-slate-100 via-gray-200/80 to-slate-200 rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-300/60 hover:border-slate-400 h-full"
      data-aos="fade-up"
    >
      <h4 className="font-bold text-gray-900 mb-3 text-base">{title}</h4>
      <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
    </div>
  );
}

function LevelItem({ title, text }) {
  return (
    <div
      className="bg-gradient-to-br from-slate-100 via-gray-200/80 to-slate-200 rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-blue-200 hover:border-blue-300 h-full"
      data-aos="fade-up"
    >
      <h4 className="font-bold text-gray-900 mb-3">{title}</h4>
      <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
    </div>
  );
}

function ListItem({ text }) {
  return (
    <li className="flex gap-2">
      <FaCheckCircle className="text-primary mt-1" />
      {text}
    </li>
  );
}

function Comparison({ label, value }) {
  return (
    <div
      className="bg-gradient-to-br from-slate-100 via-zinc-100 to-gray-200 rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-slate-300 hover:border-slate-400 h-full"
      data-aos="fade-up"
    >
      <strong className="text-gray-900 text-base block mb-2">{label}</strong>
      <div className="text-sm text-gray-600">{value}</div>
    </div>
  );
}
